import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import { axiosClient } from '../../../core/axios-client'

const BASE = '/companies/selected/geofences'
const TYPES_URL = '/companies/selected/geofences/types'
const CITIES_URL = '/companies/selected/geofences/cities'
const COUNTRIES_URL = '/companies/selected/geofences/countries'

const fetchGeofences = createAsyncThunk(
  'geofences/fetchGeofences',
  async ({ page = 1, page_size = 10, ordering = '-created_at', filters = {} } = {}) => {
    const params = { page, page_size, ordering, ...filters }
    const res = await axiosClient.get(`${BASE}`, { params })
    return res.data
  }
)

const fetchGeofence = createAsyncThunk(
  'geofences/fetchGeofence',
  async ({ id }, { rejectWithValue }) => {
    try {
      const res = await axiosClient.get(`${BASE}/${id}`)
      return res.data
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message)
    }
  }
)

const createGeofence = createAsyncThunk(
  'geofences/createGeofence',
  async ({ payload }, { rejectWithValue }) => {
    try {
      const res = await axiosClient.post(`${BASE}`, payload)
      return res.data
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message)
    }
  }
)

const updateGeofence = createAsyncThunk(
  'geofences/updateGeofence',
  async ({ id, payload }, { rejectWithValue }) => {
    try {
      const res = await axiosClient.put(`${BASE}/${id}`, payload)
      return res.data
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message)
    }
  }
)

const deleteGeofence = createAsyncThunk(
  'geofences/deleteGeofence',
  async ({ id }, { rejectWithValue }) => {
    try {
      const res = await axiosClient.delete(`${BASE}/${id}`)
      return res.data
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message)
    }
  }
)

const fetchSlowZones = createAsyncThunk(
  'geofences/fetchSlowZones',
  async (_, { rejectWithValue }) => {
    try {
      const res = await axiosClient.get(`${BASE}`, { params: { common: true, page_size: 5000 } })
      const results = res.data?.results || []
      return results.filter(g => g?.attributes?.type === 'SPEED_LIMIT')
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message)
    }
  }
)

const fetchGeofenceTypes = createAsyncThunk(
  'geofences/fetchGeofenceTypes',
  async () => {
    const res = await axiosClient.get(TYPES_URL)
    return res.data
  }
)

const fetchGeofenceCities = createAsyncThunk(
  'geofences/fetchGeofenceCities',
  async () => {
    const res = await axiosClient.get(CITIES_URL)
    return res.data
  }
)

const fetchGeofenceCountries = createAsyncThunk(
  'geofences/fetchGeofenceCountries',
  async () => {
    const res = await axiosClient.get(COUNTRIES_URL)
    return res.data
  }
)

const initialState = {
  listItems: {
    items: [],
    page: 1,
    page_size: 10,
    count: 0,
    ordering: '-created_at'
  },
  current: null,
  types: [],
  cities: [],
  countries: [],
  slowZones: {
    items: [],
    loading: false,
    loaded: false,
    error: null
  },
  loading: false,
  error: null
}

export const slice = createSlice({
  name: 'geofences',
  initialState,
  reducers: {
    setListItems(state, { payload }) {
      state.listItems = { ...state.listItems, ...payload }
    },
    resetListItems(state) {
      state.listItems = { items: [], page: 1, page_size: 10, count: 0, ordering: '-created_at' }
    },
    clearCurrentGeofence(state) {
      state.current = null
      state.error = null
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchGeofences.pending, (state) => { state.loading = true; state.error = null })
      .addCase(fetchGeofences.fulfilled, (state, { payload }) => {
        const { results = [], count = 0 } = payload || {}
        state.listItems = { ...state.listItems, items: results, count }
        state.loading = false
      })
      .addCase(fetchGeofences.rejected, (state, { payload }) => { state.loading = false; state.error = payload || 'fetch failed' })

      .addCase(fetchGeofence.pending, (state) => { state.loading = true; state.error = null })
      .addCase(fetchGeofence.fulfilled, (state, { payload }) => { state.current = payload; state.loading = false })
      .addCase(fetchGeofence.rejected, (state, { payload }) => { state.loading = false; state.error = payload || 'fetch failed' })

      .addCase(createGeofence.pending, (state) => { state.loading = true; state.error = null })
      .addCase(createGeofence.fulfilled, (state, { payload }) => { state.current = payload; state.loading = false })
      .addCase(createGeofence.rejected, (state, { payload }) => { state.loading = false; state.error = payload || 'create failed' })

      .addCase(updateGeofence.pending, (state) => { state.loading = true; state.error = null })
      .addCase(updateGeofence.fulfilled, (state, { payload }) => { state.current = payload; state.loading = false })
      .addCase(updateGeofence.rejected, (state, { payload }) => { state.loading = false; state.error = payload || 'update failed' })

      .addCase(deleteGeofence.pending, (state) => { state.loading = true; state.error = null })
      .addCase(deleteGeofence.fulfilled, (state, { meta }) => {
        state.loading = false
        const deletedId = meta?.arg?.id
          if (deletedId != null) {
            const beforeLen = state.listItems.items.length
            state.listItems.items = state.listItems.items.filter(i => i.id !== deletedId)
            if (beforeLen !== state.listItems.items.length && state.listItems.count > 0) {
              state.listItems.count = state.listItems.count - 1
            }
          }
      })
      .addCase(deleteGeofence.rejected, (state, { payload }) => { state.loading = false; state.error = payload || 'delete failed' })

      .addCase(fetchSlowZones.pending, (state) => {
        state.slowZones.loading = true
        state.slowZones.error = null
      })
      .addCase(fetchSlowZones.fulfilled, (state, { payload }) => {
        state.slowZones.items = payload
        state.slowZones.loading = false
        state.slowZones.loaded = true
      })
      .addCase(fetchSlowZones.rejected, (state, { payload }) => {
        state.slowZones.loading = false
        state.slowZones.error = payload || 'fetch failed'
      })

      .addCase(fetchGeofenceTypes.fulfilled, (state, { payload }) => {
        if (!payload) {
          state.types = []
          return
        }
        const normalized = (payload || []).map(item => {
          if (typeof item === 'string') {
            return { code: item, name: item }
          }
          return {
            code: item.code || item.id || (item.name ? String(item.name).toUpperCase() : undefined),
            name: item.name || item.code || item.id || (typeof item === 'string' ? item : '')
          }
        }).filter(t => {
          const c = (String(t.code || '')).toUpperCase()
          return c && c !== 'UNKNOWN'
        })
        state.types = normalized
      })

      .addCase(fetchGeofenceCities.fulfilled, (state, { payload }) => {
        state.cities = payload || []
      })

      .addCase(fetchGeofenceCountries.fulfilled, (state, { payload }) => {
        if (!payload) {
          state.countries = []
          return
        }
        const normalized = (payload || []).map(item => {
          if (Array.isArray(item) && item.length >= 2) {
            const [code, name] = item
            return { code: String(code), name: String(name) }
          }
          if (typeof item === 'string') {
            return { code: item, name: item }
          }
          // object shape
          return { code: item.code || item.id || item[0], name: item.name || item.label || item[1] || String(item.code || item.id || '') }
        }).filter(c => Boolean(c.code))
        state.countries = normalized
      })
  }
})

export const { setListItems, resetListItems, clearCurrentGeofence } = slice.actions

export {
  fetchGeofences,
  fetchGeofence,
  createGeofence,
  updateGeofence,
  deleteGeofence,
  fetchSlowZones,
  fetchGeofenceTypes,
  fetchGeofenceCities,
  fetchGeofenceCountries
}

export default slice.reducer
